import { AnimatePresence, motion } from 'framer-motion'
import { createContext, ReactNode, useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import { createRoot } from 'react-dom/client'

import CrossIcon from '@/assets/icons/cross.svg?react'
import { cn } from '@/utils/style'

import { Trans } from '../Trans'
import { Button } from './Button'

interface ModalProps {
  open: boolean
  children?: React.ReactNode
  className?: string
  overlayClosable?: boolean
  onClose?: () => void
}

const modalContext = createContext<ModalProps>({
  open: false,
  onClose: () => {},
})

export function Modal({ open, children, className, overlayClosable = false, onClose }: ModalProps) {
  return createPortal(
    <AnimatePresence>
      {open && (
        <modalContext.Provider
          value={{
            open,
            onClose: () => onClose?.(),
          }}
        >
          <div
            className={cn(
              'fixed top-0 bottom-0 left-0 right-0 bg-black/40 flex items-center justify-center z-50',
            )}
            onClick={overlayClosable ? onClose : undefined}
          >
            <motion.div
              className={cn(
                'w-[480px] absolute shadow-card-100 rounded-2xl overflow-hidden p-8 bg-panel-white flex flex-col gap-4',
                className,
              )}
              onClick={(e) => e.stopPropagation()}
              initial={{
                opacity: 0,
                scale: 0.75,
              }}
              animate={{
                opacity: 1,
                scale: 1,
                transition: {
                  ease: 'easeOut',
                  duration: 0.15,
                },
              }}
              exit={{
                opacity: 0,
                scale: 0.75,
                transition: {
                  ease: 'easeIn',
                  duration: 0.15,
                },
              }}
            >
              {children}
            </motion.div>
          </div>
        </modalContext.Provider>
      )}
    </AnimatePresence>,
    document.body,
  )
}

Modal.Header = function ModalHeader({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  const ctx = useContext(modalContext)

  if (!ctx) {
    throw new Error('Modal.Header should used inside Modal')
  }

  return (
    <div className={cn('text-primary h6 flex items-center justify-between', className)}>
      {children}
      <div
        className="w-12 h-12 bg-panel-gray-100 rounded-lg flex items-center justify-center cursor-pointer"
        onClick={ctx.onClose}
      >
        <CrossIcon className="w-6 h-6" />
      </div>
    </div>
  )
}

Modal.Body = function ModalHeader({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('text-base font-semibold leading-6 text-secondary', className)}>
      {children}
    </div>
  )
}

Modal.Footer = function ModalHeader({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return <div className={cn('pt-4 flex items-center justify-end gap-4', className)}>{children}</div>
}

interface ConfirmModalProps {
  title?: string
  content?: ReactNode
  onOk: () => void
  onClose: () => void
  destroy: () => void
}

interface ConfirmButtonConfig {
  type?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'outline'
    | 'danger'
    | 'warning'
    | 'link'
  text?: string
  disabled?: boolean
  minWidth?: string
}

Modal.confirm = ({
  title = 'Confirmation',
  content = 'Are you sure you want to continue?',
  confirmBtnCfg,
  className,
}: {
  title: string
  content: ReactNode
  confirmBtnCfg?: ConfirmButtonConfig
  className?: string
}) => {
  const ConfirmModal = ({ title, content, onOk, onClose, destroy }: ConfirmModalProps) => {
    const [open, setOpen] = useState(true)

    return (
      <Modal
        open={open}
        onClose={() => {
          onClose()
          setOpen(false)
          setTimeout(() => destroy(), 1000)
        }}
        className={className}
      >
        <Modal.Header>{title}</Modal.Header>
        <Modal.Body>{content}</Modal.Body>
        <Modal.Footer>
          <Button
            type="outline"
            onClick={() => {
              onClose()
              setOpen(false)
              setTimeout(() => destroy(), 1000)
            }}
            className="min-w-[88px]"
          >
            <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
          </Button>
          <Button
            type={confirmBtnCfg?.type ?? 'danger'}
            onClick={() => {
              onOk()
              setOpen(false)
            }}
            className="min-w-[88px]"
          >
            {confirmBtnCfg?.text ?? <Trans i18nKey="common.confirmModalOk">Confirm</Trans>}
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }

  return new Promise<void>((resolve, reject) => {
    const el = document.createElement('div')
    document.body.appendChild(el)
    const root = createRoot(el)
    const element = (
      <ConfirmModal
        title={title}
        content={content}
        onOk={resolve}
        onClose={reject}
        destroy={() => {
          root.unmount()
          document.body.removeChild(el)
        }}
      />
    )
    root.render(element)
  })
}
